import React, { useEffect, useState } from 'react';
import {
  Typography,
  Row,
  Col,
  Spin,
  Alert,
  Empty,
  Card,
  Tag,
  Button,
} from 'antd';
import { HeartOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../hooks/redux';
import { apiClient, Brand } from '../services/api';
import { useTranslation } from 'react-i18next';

const { Title, Text } = Typography;

const FavoritesPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAppSelector(state => state.auth);
  const { t } = useTranslation();

  const [favorites, setFavorites] = useState<Brand[]>([]);
  const [stats, setStats] = useState<{
    total: number;
    categories: Record<string, number>;
  }>({ total: 0, categories: {} });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    loadFavorites();
    loadStats();
  }, [isAuthenticated, navigate]);

  const loadFavorites = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.getFavorites();
      // 去重处理，确保没有重复的品牌ID
      const uniqueFavorites = response.data.filter(
        (brand: Brand, index: number, arr: Brand[]) =>
          arr.findIndex(b => b.id === brand.id) === index
      );
      setFavorites(uniqueFavorites);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setError(msg || t('favorites.loading_failed'));
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await apiClient.getFavoriteStats();
      setStats(response.data);
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      setError(msg || t('favorites.loading_failed'));
    }
  };

  const handleRemoveFavorite = async (brandId: number) => {
    try {
      await apiClient.removeFavorite(brandId);
      setFavorites(favorites.filter(brand => brand.id !== brandId));
      loadStats(); // 重新加载统计
    } catch (error: unknown) {
      const msg = error instanceof Error ? error.message : String(error);
      setError(msg || t('favorites.remove_failed'));
    }
  };

  const handleViewDetails = (id: number) => {
    navigate(`/brands/${id}`);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '48px' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={2}>
          <HeartOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />
          {t('favorites.title')}
        </Title>

        {stats.total > 0 && (
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              {t('favorites.total', { count: stats.total })}
            </Text>
            {Object.keys(stats.categories).length > 0 && (
              <div style={{ marginTop: 8 }}>
                <Text type="secondary">
                  {t('favorites.category_distribution')}{' '}
                </Text>
                {Object.entries(stats.categories).map(
                  ([category, count], index) => (
                    <Tag
                      key={`stats-${category}-${index}`}
                      color="blue"
                      style={{ marginBottom: 4 }}
                    >
                      {category} ({count})
                    </Tag>
                  )
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {error && (
        <Alert
          message={t('favorites.loading_failed')}
          description={error}
          type="error"
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {favorites.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('favorites.empty')}
          style={{ marginTop: 48 }}
        >
          <Button type="primary" onClick={() => navigate('/brands')}>
            {t('favorites.go_discover')}
          </Button>
        </Empty>
      ) : (
        <Row gutter={[16, 16]}>
          {favorites.map(brand => (
            <Col span={8} key={brand.id}>
              <Card
                hoverable
                onClick={() => handleViewDetails(brand.id)}
                style={{ cursor: 'pointer' }}
                actions={[
                  <Button
                    key="view"
                    type="link"
                    onClick={() => handleViewDetails(brand.id)}
                  >
                    {t('brand_detail.view_details')}
                  </Button>,
                  <Button
                    key="remove"
                    type="link"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={e => {
                      e.stopPropagation();
                      handleRemoveFavorite(brand.id);
                    }}
                  >
                    {t('favorites.remove_favorite')}
                  </Button>,
                ]}
              >
                <Card.Meta
                  title={brand.name}
                  description={
                    <div>
                      <p style={{ marginBottom: 8 }}>{brand.description}</p>
                      <p style={{ marginBottom: 8, color: '#666' }}>
                        {t('favorites.founded_short')}:{' '}
                        {new Date(brand.founded).getFullYear()} |{' '}
                        {t('favorites.headquarters_short')}:{' '}
                        {brand.headquarters}
                      </p>
                      {(() => {
                        const overall = (
                          brand as unknown as {
                            overall_score?: number;
                          }
                        ).overall_score;
                        return typeof overall === 'number';
                      })() && (
                        <div style={{ marginBottom: 8 }}>
                          <Text strong>{t('favorites.ethical_score')}: </Text>
                          <Text
                            style={{
                              color:
                                (brand as unknown as { overall_score?: number })
                                  .overall_score! >= 4
                                  ? '#52c41a'
                                  : (
                                        brand as unknown as {
                                          overall_score?: number;
                                        }
                                      ).overall_score! >= 3
                                    ? '#faad14'
                                    : '#ff4d4f',
                            }}
                          >
                            {(
                              brand as unknown as { overall_score?: number }
                            ).overall_score!.toFixed(1)}
                            /5
                          </Text>
                        </div>
                      )}
                      <div>
                        {brand.categories.map((category, index) => (
                          <Tag
                            key={`${brand.id}-${category}-${index}`}
                            color="blue"
                            style={{ marginBottom: 4 }}
                          >
                            {category}
                          </Tag>
                        ))}
                      </div>
                    </div>
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </div>
  );
};

export default FavoritesPage;
